import { c as _c } from "react-compiler-runtime";
import Raven from 'raven-js';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import getIn from 'transmute/getIn';
import memoize from 'transmute/memoize';
import once from 'transmute/once';
import { useUserInfo } from 'marketing-platform-lib/providers/UserInfoProvider';
import { getAppSettingsUIModes, getHasAppSettingsOption, useFetchFrameworkAppSettings } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import { clearQuickFetchErrorsFromWindow, getQuickFetchErrorsFromWindow } from '../../earlyRequester/utils';
import { LISTING_LIB_APP_SETTING_NAMESPACE } from '../frameworkAppSettings/constants';
import useApplicationId from '../hooks/useApplicationId';
import useFetchListingLibData from '../hooks/useFetchListingLibData';
import { jsx as _jsx } from "react/jsx-runtime";
const DataContext = /*#__PURE__*/createContext(undefined);
const trackErrors = once(() => {
  // Track any errors we encountered during the quick-fetches, except timeouts as we can't fix those.
  getQuickFetchErrorsFromWindow().forEach(error => {
    const status = getIn(['xhttp', 'status'], error);
    if (status === 0) {
      return;
    }
    Raven.captureException(error, {
      tags: {
        // @ts-ignore ts-migrate(2322) FIXME: Type 'boolean' is not assignable to type 'string'.
        fromQuickFetch: true
      },
      extra: {
        response: getIn(['xhttp', 'response'], error),
        responseText: getIn(['xhttp', 'responseText'], error),
        responseURL: getIn(['xhttp', 'responseURL'], error),
        status
      }
    });
  });
  clearQuickFetchErrorsFromWindow();
});
export default function DataProvider(t0) {
  "use memo";

  var _appSettings$LISTING_2;
  const $ = _c(32);
  const {
    children,
    showHighlySensitiveProperties
  } = t0;
  const {
    isUngatedFor,
    hasScope
  } = useUserInfo();
  const applicationId = useApplicationId();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [globalError, setGlobalError] = useState(t1);
  const {
    data: appSettings,
    loading: loadingAppSettings
  } = useFetchFrameworkAppSettings();
  let t2;
  if ($[1] !== appSettings || $[2] !== applicationId || $[3] !== hasScope || $[4] !== isUngatedFor) {
    t2 = function memoizedFn(optionName) {
      var _appSettings$LISTING_;
      return getHasAppSettingsOption(optionName, {
        isUngatedFor,
        hasScope,
        applicationId,
        appSettings: appSettings === null || appSettings === void 0 || (_appSettings$LISTING_ = appSettings[LISTING_LIB_APP_SETTING_NAMESPACE]) === null || _appSettings$LISTING_ === void 0 ? void 0 : _appSettings$LISTING_.metadata
      });
    };
    $[1] = appSettings;
    $[2] = applicationId;
    $[3] = hasScope;
    $[4] = isUngatedFor;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const memoizedFn = t2;
  const t3 = memoizedFn;
  let t4;
  if ($[6] !== t3) {
    t4 = memoize(t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const memoizedGetHasAppSettingsOption = t4;
  const t5 = appSettings === null || appSettings === void 0 || (_appSettings$LISTING_2 = appSettings[LISTING_LIB_APP_SETTING_NAMESPACE]) === null || _appSettings$LISTING_2 === void 0 ? void 0 : _appSettings$LISTING_2.metadata;
  let t6;
  if ($[8] !== applicationId || $[9] !== hasScope || $[10] !== isUngatedFor || $[11] !== t5) {
    t6 = getAppSettingsUIModes({
      isUngatedFor,
      hasScope,
      applicationId,
      appSettings: t5
    });
    $[8] = applicationId;
    $[9] = hasScope;
    $[10] = isUngatedFor;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const uiModes = t6;
  let t7;
  if ($[13] !== showHighlySensitiveProperties) {
    t7 = {
      showHighlySensitiveProperties
    };
    $[13] = showHighlySensitiveProperties;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const {
    loading: loadingListingLibData,
    properties,
    propertyGroups,
    typeDefinitions
  } = useFetchListingLibData(t7);
  let t8;
  if ($[15] !== typeDefinitions) {
    t8 = memoize(requestedObjectTypeId => {
      if (typeDefinitions.length === 0) {
        return;
      }
      return typeDefinitions.find(typeDefinition => typeDefinition.objectTypeId === requestedObjectTypeId);
    });
    $[15] = typeDefinitions;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const getTypeDefinition = t8;
  const loading = loadingAppSettings || loadingListingLibData;
  let t10;
  let t9;
  if ($[17] !== loading) {
    t9 = () => {
      if (!loading) {
        trackErrors();
      }
    };
    t10 = [loading];
    $[17] = loading;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  let t11;
  if ($[20] !== getTypeDefinition || $[21] !== globalError || $[22] !== loading || $[23] !== memoizedGetHasAppSettingsOption || $[24] !== properties || $[25] !== propertyGroups || $[26] !== typeDefinitions || $[27] !== uiModes) {
    t11 = {
      getTypeDefinition,
      getHasAppSettingsOption: memoizedGetHasAppSettingsOption,
      globalError,
      loading,
      properties,
      propertyGroups,
      setGlobalError,
      typeDefinitions,
      uiModes
    };
    $[20] = getTypeDefinition;
    $[21] = globalError;
    $[22] = loading;
    $[23] = memoizedGetHasAppSettingsOption;
    $[24] = properties;
    $[25] = propertyGroups;
    $[26] = typeDefinitions;
    $[27] = uiModes;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const value = t11;
  let t12;
  if ($[29] !== children || $[30] !== value) {
    t12 = /*#__PURE__*/_jsx(DataContext.Provider, {
      value: value,
      children: children
    });
    $[29] = children;
    $[30] = value;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
}
export function useListingLibData() {
  "use memo";

  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useListingLibData must be used within a DataProvider");
  }
  return context;
}
export function useGlobalError() {
  "use memo";

  const $ = _c(3);
  const {
    globalError,
    setGlobalError
  } = useListingLibData();
  let t0;
  if ($[0] !== globalError || $[1] !== setGlobalError) {
    t0 = [globalError, setGlobalError];
    $[0] = globalError;
    $[1] = setGlobalError;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}